import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Storage as StorageIcon,
  AccessTime as AccessTimeIcon,
  Person as PersonIcon,
  Folder as FolderIcon,
  InsertDriveFile as FileIcon,
  Refresh as RefreshIcon,
  Analytics as AnalyticsIcon,
  Timeline as TimelineIcon,
} from '@mui/icons-material';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
} from 'recharts';
import { Link as RouterLink } from 'react-router-dom';
import PageHeader from '../../components/ui/PageHeader';
import EmptyState from '../../components/ui/EmptyState';
import analyticsService, { UsageAnalytics as UsageAnalyticsData } from '../../services/analyticsService';
import usePageTitle from '../../hooks/usePageTitle';

const COLORS = ['#3f51b5', '#00897b', '#f9a825', '#e64a19', '#7b1fa2', '#0288d1'];

const emptyUsage = (): UsageAnalyticsData => ({
  storage: {
    total: 0,
    used: 0,
    available: 0,
    percentage: 0,
    fileCount: 0,
    byFileType: [],
    recentGrowth: [],
  },
  activity: {
    totalActivities: 0,
    countsByType: {},
    activityByDay: [],
    mostActiveResources: [],
  },
  topFiles: [],
  topFolders: [],
  userActivity: [],
  timeSeriesData: [],
});

const formatBytes = (bytes: number): string => {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

const formatDate = (dateString?: string | null): string => {
  if (!dateString) return '—';
  const d = new Date(dateString);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
};

const ChartEmpty: React.FC<{ message: string }> = ({ message }) => (
  <Box
    sx={{
      height: 260,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      border: '1px dashed',
      borderColor: 'divider',
      borderRadius: 1,
      bgcolor: 'background.default',
    }}
  >
    <Typography variant="body2" color="text.secondary">
      {message}
    </Typography>
  </Box>
);

const sectionPaperSx = {
  p: 2.5,
  height: '100%',
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 2,
} as const;

const UsageAnalytics: React.FC = () => {
  usePageTitle('Usage Analytics');
  const [usageData, setUsageData] = useState<UsageAnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timeRange, setTimeRange] = useState('7d');
  const [refreshing, setRefreshing] = useState(false);

  const days = parseInt(timeRange.replace('d', ''), 10) || 7;

  const fetchUsageData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      try {
        const data = await analyticsService.getUsageAnalytics(timeRange);
        setUsageData({
          ...emptyUsage(),
          ...data,
          storage: { ...emptyUsage().storage, ...(data.storage || {}) },
          activity: { ...emptyUsage().activity, ...(data.activity || {}) },
          topFiles: data.topFiles || [],
          topFolders: data.topFolders || [],
          userActivity: data.userActivity || [],
          timeSeriesData: data.timeSeriesData || [],
        });
        return;
      } catch {
        // Fall back to individual endpoints if the aggregate call fails
      }

      const [storage, activity, topFiles, topFolders, userActivity, timeSeriesData] =
        await Promise.all([
          analyticsService.getStorageAnalytics().catch(() => emptyUsage().storage),
          analyticsService.getActivityAnalytics(days).catch(() => emptyUsage().activity),
          analyticsService.getTopFiles(10).catch(() => []),
          analyticsService.getTopFolders(10).catch(() => []),
          analyticsService.getUserActivity(10).catch(() => []),
          analyticsService.getTimeSeriesData(days).catch(() => []),
        ]);

      setUsageData({
        storage,
        activity,
        topFiles: topFiles || [],
        topFolders: topFolders || [],
        userActivity: userActivity || [],
        timeSeriesData: timeSeriesData || [],
      });
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load usage analytics');
      setUsageData(null);
    } finally {
      setLoading(false);
    }
  }, [timeRange, days]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchUsageData();
    setRefreshing(false);
  };

  useEffect(() => {
    fetchUsageData();
  }, [fetchUsageData]);

  if (loading && !usageData) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 360 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !usageData) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={fetchUsageData}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      </Box>
    );
  }

  if (!usageData) {
    return (
      <Box sx={{ p: 3 }}>
        <EmptyState
          type="default"
          title="No analytics yet"
          description="Usage data will appear here as you upload and work with files."
        />
      </Box>
    );
  }

  const activityChartData =
    usageData.activity.activityByDay?.map((item) => ({
      date: formatDate(item.date),
      activities: item.count,
    })) || [];

  const storageByTypeData =
    usageData.storage.byFileType?.map((item, index) => ({
      name: item.type || 'unknown',
      value: item.size,
      color: COLORS[index % COLORS.length],
    })) || [];

  const activeUsers = usageData.userActivity?.length || 0;
  const folderCount = usageData.topFolders?.length || 0;

  return (
    <Box sx={{ p: 3, maxWidth: '100%' }}>
      <PageHeader
        title="Usage Analytics"
        icon={<AnalyticsIcon fontSize="large" color="primary" />}
        description="Storage, activity, and usage trends for your drive"
        actions={
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Time range</InputLabel>
              <Select
                value={timeRange}
                label="Time range"
                onChange={(e) => setTimeRange(e.target.value)}
              >
                <MenuItem value="7d">Last 7 days</MenuItem>
                <MenuItem value="30d">Last 30 days</MenuItem>
                <MenuItem value="90d">Last 90 days</MenuItem>
              </Select>
            </FormControl>
            <Button
              component={RouterLink}
              to="/activity"
              size="small"
              variant="outlined"
              startIcon={<TimelineIcon />}
            >
              Activity
            </Button>
            <Tooltip title="Refresh">
              <span>
                <IconButton onClick={handleRefresh} disabled={refreshing || loading} size="small">
                  {refreshing || loading ? <CircularProgress size={20} /> : <RefreshIcon />}
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        }
      />

      {error && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2.5}>
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <StorageIcon color="primary" fontSize="small" />
                <Typography variant="subtitle2" color="text.secondary">
                  Storage used
                </Typography>
              </Box>
              <Typography variant="h5" fontWeight={600}>
                {formatBytes(usageData.storage.used || 0)}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                of {formatBytes(usageData.storage.total || 0)} ·{' '}
                {(usageData.storage.fileCount || 0).toLocaleString()} files
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <AccessTimeIcon color="primary" fontSize="small" />
                <Typography variant="subtitle2" color="text.secondary">
                  Activities
                </Typography>
              </Box>
              <Typography variant="h5" fontWeight={600}>
                {(usageData.activity.totalActivities || 0).toLocaleString()}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                last {timeRange}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <FolderIcon color="primary" fontSize="small" />
                <Typography variant="subtitle2" color="text.secondary">
                  Top folders
                </Typography>
              </Box>
              <Typography variant="h5" fontWeight={600}>
                {folderCount}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                by file count
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <PersonIcon color="primary" fontSize="small" />
                <Typography variant="subtitle2" color="text.secondary">
                  Active users
                </Typography>
              </Box>
              <Typography variant="h5" fontWeight={600}>
                {activeUsers}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                with activity in range
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={8}>
          <Paper elevation={0} sx={sectionPaperSx}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Activity over time
            </Typography>
            {activityChartData.length === 0 ? (
              <ChartEmpty message="No activity in this period" />
            ) : (
              <Box sx={{ width: '100%', height: 280 }}>
                <ResponsiveContainer>
                  <LineChart data={activityChartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                    <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                    <RechartsTooltip />
                    <Line
                      type="monotone"
                      dataKey="activities"
                      stroke="#3f51b5"
                      strokeWidth={2}
                      dot={{ r: 3 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper elevation={0} sx={sectionPaperSx}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Storage by type
            </Typography>
            {storageByTypeData.length === 0 ? (
              <ChartEmpty message="No files uploaded yet" />
            ) : (
              <Box sx={{ width: '100%', height: 280 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie
                      data={storageByTypeData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) =>
                        `${name} ${((percent || 0) * 100).toFixed(0)}%`
                      }
                      outerRadius={80}
                      dataKey="value"
                    >
                      {storageByTypeData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <RechartsTooltip formatter={(value: number) => formatBytes(value)} />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={sectionPaperSx}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Most accessed files
            </Typography>
            {(usageData.topFiles || []).length === 0 ? (
              <ChartEmpty message="No file access data yet" />
            ) : (
              <List dense disablePadding>
                {usageData.topFiles.map((file, index) => (
                  <React.Fragment key={file.id || `${file.name}-${index}`}>
                    <ListItem
                      secondaryAction={
                        file.type ? (
                          <Chip label={String(file.type).toUpperCase()} size="small" variant="outlined" />
                        ) : null
                      }
                      sx={{ px: 0 }}
                    >
                      <ListItemAvatar>
                        <Avatar sx={{ width: 36, height: 36, bgcolor: 'action.hover', color: 'text.secondary' }}>
                          <FileIcon fontSize="small" />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={file.name}
                        primaryTypographyProps={{ noWrap: true, fontWeight: 500 }}
                        secondary={`${formatBytes(file.size || 0)} · ${file.accessCount || 0} accesses · ${formatDate(file.lastAccessed)}`}
                      />
                    </ListItem>
                    {index < usageData.topFiles.length - 1 && <Divider component="li" />}
                  </React.Fragment>
                ))}
              </List>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={sectionPaperSx}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Most active folders
            </Typography>
            {(usageData.topFolders || []).length === 0 ? (
              <ChartEmpty message="No folder data yet" />
            ) : (
              <List dense disablePadding>
                {usageData.topFolders.map((folder, index) => (
                  <React.Fragment key={folder.id || `${folder.name}-${index}`}>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemAvatar>
                        <Avatar sx={{ width: 36, height: 36, bgcolor: 'action.hover', color: 'text.secondary' }}>
                          <FolderIcon fontSize="small" />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={folder.name}
                        primaryTypographyProps={{ noWrap: true, fontWeight: 500 }}
                        secondary={`${folder.fileCount || 0} files · ${formatBytes(folder.size || 0)} · ${formatDate(folder.lastModified)}`}
                      />
                    </ListItem>
                    {index < usageData.topFolders.length - 1 && <Divider component="li" />}
                  </React.Fragment>
                ))}
              </List>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={sectionPaperSx}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              User activity
            </Typography>
            {(usageData.userActivity || []).length === 0 ? (
              <ChartEmpty message="No user activity in this period" />
            ) : (
              <Box sx={{ width: '100%', height: 280 }}>
                <ResponsiveContainer>
                  <BarChart data={usageData.userActivity} margin={{ top: 8, right: 8, left: 0, bottom: 48 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                    <XAxis dataKey="userName" tick={{ fontSize: 11 }} angle={-25} textAnchor="end" interval={0} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                    <RechartsTooltip />
                    <Bar dataKey="activityCount" fill="#3f51b5" name="Activities" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={sectionPaperSx}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Activity breakdown
            </Typography>
            {(usageData.timeSeriesData || []).length === 0 ? (
              <ChartEmpty message="No breakdown data for this period" />
            ) : (
              <Box sx={{ width: '100%', height: 280 }}>
                <ResponsiveContainer>
                  <BarChart data={usageData.timeSeriesData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 11 }}
                      tickFormatter={(v) => formatDate(v)}
                    />
                    <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                    <RechartsTooltip labelFormatter={(v) => formatDate(String(v))} />
                    <Legend />
                    <Bar dataKey="uploads" fill="#4caf50" name="Uploads" stackId="a" />
                    <Bar dataKey="downloads" fill="#2196f3" name="Downloads" stackId="a" />
                    <Bar dataKey="shares" fill="#ff9800" name="Shares" stackId="a" />
                    <Bar dataKey="deletions" fill="#f44336" name="Deletions" stackId="a" />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default UsageAnalytics;
